import { format, subDays, eachDayOfInterval, startOfMonth, subMonths, eachMonthOfInterval } from 'date-fns'
import { supabase } from './supabase'
import { calculateIncomeStatement } from './financialStatements'
import { resolveAccountsByMappingKeys } from './accountingService'
import { getAllCustomerBalances, getAllSupplierBalances } from './contactBalances'
import { fetchAccountLines } from './agedLedger'
import { fetchAllPaged, fetchByIdsChunked } from './pagination'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (table: string): any => (supabase as any).from(table)

// ─── Dashboard ────────────────────────────────────────────────────────────────

export async function fetchSalesForPeriod(tenantId: string, from: string, to: string) {
  return await fetchAllPaged<{
    id: string; total: number; amount_paid: number; payment_status: string
    created_at: string; order_number: string; customer_id: string | null
  }>((pageFrom, pageTo) =>
    db('sales_orders')
      .select('id, total, amount_paid, payment_status, created_at, order_number, customer_id')
      .eq('tenant_id', tenantId)
      .neq('status', 'cancelled')
      .gte('created_at', from + 'T00:00:00')
      .lte('created_at', to + 'T23:59:59')
      .order('created_at', { ascending: false })
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))
}

export async function fetchSalesForYear(tenantId: string, year: number) {
  return await fetchAllPaged<{ total: number; created_at: string }>((from, to) =>
    db('sales_orders')
      .select('total, created_at')
      .eq('tenant_id', tenantId)
      .neq('status', 'cancelled')
      .gte('created_at', `${year}-01-01T00:00:00`)
      .lte('created_at', `${year}-12-31T23:59:59`)
      .order('id', { ascending: true })
      .range(from, to))
}

export async function fetchLast30DaysSales(tenantId: string) {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 29)
  return await fetchAllPaged<{ total: number; created_at: string }>((pageFrom, pageTo) =>
    db('sales_orders')
      .select('total, created_at')
      .eq('tenant_id', tenantId)
      .neq('status', 'cancelled')
      .gte('created_at', from.toISOString().slice(0, 10) + 'T00:00:00')
      .lte('created_at', to.toISOString().slice(0, 10) + 'T23:59:59')
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))
}

export async function fetchDebtorTotal(tenantId: string): Promise<number> {
  const rows = await fetchAllPaged<{ total: number | null; amount_paid: number | null }>((from, to) =>
    db('sales_orders').select('total, amount_paid').eq('tenant_id', tenantId)
      .not('payment_status', 'in', '("cancelled","refunded","paid")')
      .order('id', { ascending: true }).range(from, to))
  return rows.reduce((sum, r) => sum + Math.max(0, (r.total ?? 0) - (r.amount_paid ?? 0)), 0)
}

export async function fetchCreditorTotal(tenantId: string): Promise<number> {
  const rows = await fetchAllPaged<{ total: number | null; amount_paid: number | null }>((from, to) =>
    db('purchase_orders').select('total, amount_paid').eq('tenant_id', tenantId)
      .not('payment_status', 'in', '("cancelled","refunded","paid")')
      .order('id', { ascending: true }).range(from, to))
  return rows.reduce((sum, r) => sum + Math.max(0, (r.total ?? 0) - (r.amount_paid ?? 0)), 0)
}

export async function fetchLowStockCount(tenantId: string): Promise<number> {
  const data = await fetchAllPaged<{ quantity: number; reorder_point: number; reorder_level: number }>((from, to) =>
    db('products')
      .select('id, quantity, reorder_point, reorder_level')
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .order('id', { ascending: true })
      .range(from, to))

  return data
    .filter(p => {
      const qty = p.quantity ?? 0
      const threshold = Math.max(p.reorder_point ?? 0, p.reorder_level ?? 0)
      return qty <= 0 || (threshold > 0 && qty <= threshold)
    }).length
}

export async function fetchTopItems(tenantId: string, from: string, to: string, limit = 5) {
  const orders = await fetchAllPaged<{ id: string }>((pageFrom, pageTo) =>
    db('sales_orders')
      .select('id')
      .eq('tenant_id', tenantId)
      .neq('status', 'cancelled')
      .gte('created_at', from + 'T00:00:00')
      .lte('created_at', to + 'T23:59:59')
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))

  const orderIds = orders.map((o) => o.id)
  if (orderIds.length === 0) return []

  const items = await fetchByIdsChunked<{ product_id: string; quantity: number; products: { name: string } | null }>(orderIds, (batch) =>
    db('sales_order_items')
      .select('product_id, quantity, products(name)')
      .in('sales_order_id', batch))

  const grouped = new Map<string, { name: string; qty: number }>()
  for (const item of items) {
    const pid = item.product_id
    const existing = grouped.get(pid) ?? { name: item.products?.name ?? pid, qty: 0 }
    grouped.set(pid, { ...existing, qty: existing.qty + (item.quantity ?? 0) })
  }

  return Array.from(grouped.values())
    .sort((a, b) => b.qty - a.qty)
    .slice(0, limit)
}

export async function fetchRecentOrders(tenantId: string, limit = 6) {
  const { data, error } = await db('sales_orders')
    .select('id, order_number, total, amount_paid, payment_status, created_at, customers(name)')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; order_number: string; total: number; amount_paid: number
    payment_status: string; created_at: string; customers: { name: string } | null
  }>
}

// ─── Products ─────────────────────────────────────────────────────────────────

export async function fetchProducts(tenantId: string, search?: string, limit = 50, offset = 0) {
  let q = db('products')
    .select('id, name, sku, barcode, selling_price, cost_price, reorder_level, is_active, product_categories(name), product_units(abbreviation), vat_categories(id,code,name,is_vatable)', { count: 'exact' })
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .order('name')
    .range(offset, offset + limit - 1)

  if (search) q = q.ilike('name', `%${search}%`)
  const { data, error, count } = await q
  if (error) throw error
  return {
    data: (data ?? []) as Array<{
      id: string; name: string; sku: string | null; barcode: string | null
      selling_price: number; cost_price: number; reorder_level: number; is_active: boolean
      vat_categories: { id: string; code: string; name: string; is_vatable: boolean } | null
      product_categories: { name: string } | null
      product_units: { abbreviation: string } | null
    }>,
    total: count ?? 0,
  }
}

// ─── Sales ────────────────────────────────────────────────────────────────────

export async function fetchSalesOrders(tenantId: string, search?: string, limit = 30) {
  let q = db('sales_orders')
    .select('id, order_number, total, amount_paid, payment_status, status, created_at, customers(name)')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (search) q = q.ilike('order_number', `%${search}%`)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; order_number: string; total: number; amount_paid: number
    payment_status: string; status: string; created_at: string
    customers: { name: string } | null
  }>
}

// ─── Purchases ────────────────────────────────────────────────────────────────

export async function fetchPurchaseOrders(tenantId: string, search?: string, limit = 30) {
  let q = db('purchase_orders')
    .select('id, order_number, total, amount_paid, payment_status, status, created_at, suppliers(name)')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (search) q = q.ilike('order_number', `%${search}%`)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; order_number: string; total: number; amount_paid: number
    payment_status: string; status: string; created_at: string
    suppliers: { name: string } | null
  }>
}

// ─── Expenses ─────────────────────────────────────────────────────────────────

export async function fetchExpenses(tenantId: string, limit = 30) {
  const { data, error } = await db('expenses')
    .select('id, amount, description, expense_date, payment_method, created_at, expense_categories(name)')
    .eq('tenant_id', tenantId)
    .order('expense_date', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; amount: number; description: string | null; expense_date: string
    payment_method: string | null; created_at: string
    expense_categories: { name: string } | null
  }>
}

// ─── Contacts ─────────────────────────────────────────────────────────────────

export async function fetchCustomers(tenantId: string, search?: string, limit = 50, offset = 0) {
  let q = db('customers')
    .select('id, name, email, phone, outstanding_balance, credit_limit')
    .eq('tenant_id', tenantId)
    .order('name')
    .range(offset, offset + limit - 1)

  if (search) q = q.ilike('name', `%${search}%`)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; name: string; email: string | null; phone: string | null
    outstanding_balance: number; credit_limit: number
  }>
}

export async function fetchSuppliers(tenantId: string, search?: string, limit = 50, offset = 0) {
  let q = db('suppliers')
    .select('id, name, business_name, email, phone, outstanding_balance')
    .eq('tenant_id', tenantId)
    .order('name')
    .range(offset, offset + limit - 1)

  if (search) q = q.or(`name.ilike.%${search}%,business_name.ilike.%${search}%`)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; name: string; business_name: string | null; email: string | null; phone: string | null
    outstanding_balance: number
  }>
}


// ─── Warehouses ───────────────────────────────────────────────────────────────

export async function fetchWarehouses(tenantId: string) {
  const { data, error } = await db('warehouses')
    .select('id, name, location, is_default, is_active, created_at')
    .eq('tenant_id', tenantId)
    .order('is_default', { ascending: false })
    .order('name')
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; name: string; location: string | null
    is_default: boolean; is_active: boolean; created_at: string
  }>
}

// ─── Notifications (in Database typedef) ─────────────────────────────────────

export async function fetchNotifications(tenantId: string) {
  const { data, error } = await db('notifications')
    .select('id, title, message, type, is_read, link, created_at')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
    .limit(50)
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; title: string; message: string
    type: 'info' | 'warning' | 'error' | 'success'
    is_read: boolean; link: string | null; created_at: string
  }>
}

// ─── Audit (in Database typedef) ─────────────────────────────────────────────

export async function fetchAuditLogs(tenantId: string) {
  const { data, error } = await db('audit_logs')
    .select('id, action, table_name, resource_type, description, record_id, ip_address, created_at, user_id')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
    .limit(100)
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; action: string; table_name: string; resource_type: string | null
    description: string | null; record_id: string | null
    ip_address: string | null; created_at: string; user_id: string | null
  }>
}

// Resolve a set of user ids to display names for one tenant. Coalesce
// full_name → username → first+last → email-local → 'Unknown'. Shared by the audit
// trail (and reusable anywhere a stored actor/created_by id must show as a name).
export async function resolveUserNames(tenantId: string, userIds: string[]): Promise<Map<string, string>> {
  const ids = Array.from(new Set(userIds.filter(Boolean)))
  const out = new Map<string, string>()
  if (!tenantId || ids.length === 0) return out
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200)
    const { data, error } = await db('tenant_users')
      .select('user_id, full_name, username, first_name, last_name, email')
      .eq('tenant_id', tenantId)
      .in('user_id', chunk)
    if (error) throw error
    for (const r of (data ?? []) as Array<{ user_id: string; full_name: string | null; username: string | null; first_name: string | null; last_name: string | null; email: string | null }>) {
      const first = (r.first_name ?? '').trim()
      const last = (r.last_name ?? '').trim()
      const name =
        (r.full_name && r.full_name.trim()) ||
        (r.username && r.username.trim()) ||
        ((first || last) ? `${first} ${last}`.trim() : '') ||
        (r.email ? r.email.split('@')[0] : '') ||
        'Unknown'
      out.set(r.user_id, name)
    }
  }
  return out
}

// ─── Stock ────────────────────────────────────────────────────────────────────

// ─── Product Categories ───────────────────────────────────────────────────────

export async function fetchProductCategories(tenantId: string) {
  const { data, error } = await db('product_categories')
    .select('id, name, color')
    .eq('tenant_id', tenantId)
    .order('name')
  if (error) throw error
  return (data ?? []) as Array<{ id: string; name: string; color: string }>
}

// ─── Expense Categories + Items ───────────────────────────────────────────────

export async function fetchExpenseCategoriesWithItems(tenantId: string) {
  const { data, error } = await db('expense_categories')
    .select('id, name, color, description, is_active, gl_account_id, gl_account:accounts(id, code, name), expense_items(id, name, is_active)')
    .eq('tenant_id', tenantId)
    .order('name')
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; name: string; color: string
    description: string | null; is_active: boolean
    gl_account_id: string | null
    gl_account: { id: string; code: string; name: string } | null
    expense_items: Array<{ id: string; name: string; is_active: boolean }>
  }>
}

export async function fetchPostableExpenseAccounts(tenantId: string) {
  const { data, error } = await db('accounts')
    .select('id, code, name')
    .eq('tenant_id', tenantId)
    .eq('account_type', 'expense')
    .eq('allow_direct_posting', true)
    .eq('is_active', true)
    .order('code')
  if (error) throw error
  return (data ?? []) as Array<{ id: string; code: string; name: string }>
}

// ─── Payment Accounts ─────────────────────────────────────────────────────────

export async function fetchPaymentAccounts(tenantId: string) {
  const { getPaymentAccounts } = await import('./getPaymentAccounts')
  return getPaymentAccounts(supabase, tenantId)
}

export async function fetchReceivingAccounts(tenantId: string) {
  const { getReceivingAccounts } = await import('./getPaymentAccounts')
  return getReceivingAccounts(supabase, tenantId)
}

// ─── POS Holds ────────────────────────────────────────────────────────────────

export async function fetchPosHolds(tenantId: string) {
  const { data, error } = await db('pos_holds')
    .select('id, label, customer_id, customer_name, cart_items, discount_amount, subtotal, total, created_by, created_at')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; label: string; customer_id: string | null; customer_name: string | null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cart_items: any[]; discount_amount: number; subtotal: number; total: number
    created_by: string | null; created_at: string
  }>
}

// ─── Tenant Users (for User Management) ──────────────────────────────────────

export async function fetchTenantUsers(tenantId: string) {
  const { data, error } = await db('tenant_users')
    .select(`
      id, user_id, role, role_id, is_active,
      full_name, first_name, last_name, email, username, phone,
      staff_id, created_at,
      staff ( personnel_number, full_name )
    `)
    .eq('tenant_id', tenantId)
    .order('created_at')
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; user_id: string; role: string; role_id: string | null; is_active: boolean
    full_name: string | null; first_name: string | null; last_name: string | null
    email: string | null; username: string | null; phone: string | null
    staff_id: string | null
    staff: { personnel_number: number; full_name: string } | null
    created_at: string
  }>
}

export async function fetchRoles(tenantId: string) {
  const { data, error } = await db('roles')
    .select('id, name, description, is_system, created_at')
    .eq('tenant_id', tenantId)
    .order('is_system', { ascending: false })
    .order('name')
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; name: string; description: string | null; is_system: boolean; created_at: string
  }>
}

export async function fetchRolePermissions(roleId: string) {
  const { data, error } = await db('role_permissions')
    .select('permission, granted')
    .eq('role_id', roleId)
    .eq('granted', true)
  if (error) throw error
  return (data ?? []).map((r: { permission: string }) => r.permission) as string[]
}

export async function fetchUserModulePermissions(tenantId: string, userId: string) {
  const { data, error } = await db('user_module_permissions')
    .select('permissions')
    .eq('tenant_id', tenantId)
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return (data as { permissions: Record<string, boolean> } | null)?.permissions ?? null
}

// ─── Tax Rates ────────────────────────────────────────────────────────────────

export async function fetchTaxRates(tenantId: string) {
  const { data, error } = await db('tax_rates')
    .select('id, name, rate, is_active')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .order('rate')
  if (error) throw error
  return (data ?? []) as Array<{ id: string; name: string; rate: number; is_active: boolean }>
}

// ─── Sales (enhanced) ─────────────────────────────────────────────────────────

export type SalesOrderFilters = {
  search?: string
  from?: string
  to?: string
  paymentStatus?: string
  paymentMethod?: string
  statusFilter?: 'active' | 'cancelled' | 'draft' | 'quotation' | 'returned' | 'all'
  locationId?: string
  createdBy?: string
}

export type SalesOrderRow = {
  id: string; order_number: string; status: string; subtotal: number | null
  total: number; amount_paid: number; payment_status: string
  payment_method: string | null; sale_type: string | null
  created_at: string; payment_due_date: string | null
  delivery_fee: number | null; payment_indicated: boolean | null
  customer_id: string | null
  location_id: string | null
  share_token: string | null
  location: { id: string; name: string } | null
  customers: { id: string; name: string; business_name: string | null; phone: string | null; email: string | null } | null
  non_refund_returns_total: number
  // Distinct payment methods actually applied to this sale (from order_payments,
  // non-voided/non-detached — the same rows the Sales Payment report reads). Empty
  // when nothing has been paid. Drives the "Mode of Payment" column.
  payment_methods: string[]
  meta: { opening_balance_bf?: boolean } | null
}

const SALES_ORDERS_SELECT =
  'id, order_number, status, subtotal, total, amount_paid, payment_status, payment_method, sale_type, created_at, payment_due_date, delivery_fee, payment_indicated, customer_id, location_id, share_token, meta, location:warehouses!location_id(id, name), customers(id, name, business_name, phone, email)'

// Resolve the search term to matching customer ids ONCE, so the filter can be
// applied synchronously (and repeatedly, per page) without re-querying customers.
// customerIds === null → no search active.
async function resolveSalesFilterCtx(
  tenantId: string,
  filters: SalesOrderFilters,
): Promise<{ customerIds: string[] | null }> {
  if (!filters.search) return { customerIds: null }
  const s = filters.search
  const { data } = await db('customers')
    .select('id')
    .eq('tenant_id', tenantId)
    .or(`name.ilike.%${s}%,business_name.ilike.%${s}%,phone.ilike.%${s}%`)
  return { customerIds: (data ?? []).map((c: { id: string }) => c.id) }
}

// Apply tenant + every list filter to a query. Synchronous, so it can run fresh
// on each page inside fetchAllPaged (the search→customer-ids lookup is resolved
// once, up front, into ctx).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applySalesFilters(q: any, tenantId: string, filters: SalesOrderFilters, ctx: { customerIds: string[] | null }): any {
  q = q.eq('tenant_id', tenantId)
  if (filters.search) {
    const s = filters.search
    if (ctx.customerIds && ctx.customerIds.length > 0) {
      q = q.or(`order_number.ilike.%${s}%,customer_id.in.(${ctx.customerIds.join(',')})`)
    } else {
      q = q.ilike('order_number', `%${s}%`)
    }
  }
  if (filters.from) q = q.gte('created_at', filters.from + 'T00:00:00')
  if (filters.to) q = q.lte('created_at', filters.to + 'T23:59:59')
  if (filters.paymentStatus && filters.paymentStatus !== 'all') q = q.eq('payment_status', filters.paymentStatus)
  if (filters.locationId) q = q.eq('location_id', filters.locationId)
  if (filters.createdBy) q = q.eq('created_by', filters.createdBy)

  // When a search term is present, ignore the status tab and match across ALL statuses,
  // so an invoice is never unfindable from whichever tab happens to be selected (a
  // returned / partially-returned sale used to be invisible from Active). With no search,
  // the selected tab's predicate applies as normal.
  if (!filters.search) {
    const sf = filters.statusFilter ?? 'active'
    // 'active' includes partially_returned — it still owes a balance and is still
    // returnable (SalesPage canReturn treats it as actionable).
    if (sf === 'active') q = q.in('status', ['final', 'pro_forma', 'on_hold', 'partially_returned'])
    else if (sf === 'returned') q = q.in('status', ['returned', 'partially_returned'])
    else if (sf === 'cancelled') q = q.eq('status', 'cancelled')
    else if (sf === 'draft') q = q.eq('status', 'draft')
    else if (sf === 'quotation') q = q.eq('status', 'quotation')
    // 'all' → no status filter
  }
  return q
}

// One page of enhanced sales rows + the exact total count for the same filters.
// Server-side paginated: pass 1-based `page` and `pageSize`; the caller drives a
// "Page X of N" pager off `count` (replaces the old silent 100-row cap).
export async function fetchSalesOrdersEnhanced(
  tenantId: string,
  filters: SalesOrderFilters = {},
  pagination: { page?: number; pageSize?: number } = {},
): Promise<{ rows: SalesOrderRow[]; count: number }> {
  const page = Math.max(1, pagination.page ?? 1)
  const pageSize = pagination.pageSize ?? 50
  const ctx = await resolveSalesFilterCtx(tenantId, filters)

  let q = db('sales_orders').select(SALES_ORDERS_SELECT, { count: 'exact' })
  q = applySalesFilters(q, tenantId, filters, ctx)
  const { data, error, count } = await q
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1)
  if (error) throw error
  const rows = (data ?? []) as SalesOrderRow[]

  const returnsBySale = await fetchNonRefundReturnsTotals(tenantId, rows.map(r => r.id))
  for (const row of rows) row.non_refund_returns_total = returnsBySale[row.id] ?? 0
  const methodsBySale = await fetchSalePaymentMethodsBySale(tenantId, rows.map(r => r.id))
  for (const row of rows) row.payment_methods = methodsBySale[row.id] ?? []
  return { rows, count: count ?? 0 }
}

// Distinct payment methods applied to each sale, from order_payments (non-voided,
// non-detached — the rows the Sales Payment report treats as real receipts). Returns
// order_id → distinct method strings. Chunked over the sale ids (bounded read).
export async function fetchSalePaymentMethodsBySale(
  tenantId: string,
  saleIds: string[],
): Promise<Record<string, string[]>> {
  if (saleIds.length === 0) return {}
  const rows = await fetchByIdsChunked<{ order_id: string; payment_method: string | null }>(saleIds, (batch) =>
    db('order_payments')
      .select('order_id, payment_method')
      .eq('tenant_id', tenantId)
      .in('order_id', batch)
      .not('is_voided', 'is', true)
      .not('is_detached', 'is', true))
  const out: Record<string, string[]> = {}
  for (const r of rows) {
    if (!r.order_id) continue
    const m = (r.payment_method ?? '').trim() || 'unknown'
    const list = (out[r.order_id] ??= [])
    if (!list.includes(m)) list.push(m)
  }
  return out
}

// Footer total: sum of `total` across ALL rows matching the current filters,
// excluding cancelled sales. Paged so it never truncates on large tenants.
export async function fetchSalesOrdersActiveTotal(
  tenantId: string,
  filters: SalesOrderFilters = {},
): Promise<number> {
  const ctx = await resolveSalesFilterCtx(tenantId, filters)
  const rows = await fetchAllPaged<{ status: string; total: number | null }>((from, to) =>
    applySalesFilters(db('sales_orders').select('status, total'), tenantId, filters, ctx)
      .order('id', { ascending: true })
      .range(from, to))
  return rows
    .filter(r => r.status !== 'cancelled')
    .reduce((s, r) => s + (r.total ?? 0), 0)
}

// Every row matching the current filters, for "export the whole view". Paged by
// id (safe, unique) then sorted newest-first for the file.
export async function fetchSalesOrdersForExport(
  tenantId: string,
  filters: SalesOrderFilters = {},
): Promise<SalesOrderRow[]> {
  const ctx = await resolveSalesFilterCtx(tenantId, filters)
  const rows = await fetchAllPaged<SalesOrderRow>((from, to) =>
    applySalesFilters(db('sales_orders').select(SALES_ORDERS_SELECT), tenantId, filters, ctx)
      .order('id', { ascending: true })
      .range(from, to))
  const returnsBySale = await fetchNonRefundReturnsTotals(tenantId, rows.map(r => r.id))
  for (const row of rows) row.non_refund_returns_total = returnsBySale[row.id] ?? 0
  const methodsBySale = await fetchSalePaymentMethodsBySale(tenantId, rows.map(r => r.id))
  for (const row of rows) row.payment_methods = methodsBySale[row.id] ?? []
  rows.sort((a, b) => b.created_at.localeCompare(a.created_at))
  return rows
}

export async function fetchNonRefundReturnsTotals(
  tenantId: string,
  saleIds: string[],
): Promise<Record<string, number>> {
  if (saleIds.length === 0) return {}
  const data = await fetchByIdsChunked<{ original_sale_id: string; total_amount: number }>(saleIds, (batch) =>
    db('sale_returns')
      .select('original_sale_id, total_amount')
      .eq('tenant_id', tenantId)
      .in('original_sale_id', batch)
      .neq('return_type', 'refund')
      .neq('status', 'rejected'))
  const result: Record<string, number> = {}
  for (const r of data) {
    result[r.original_sale_id] = (result[r.original_sale_id] ?? 0) + (r.total_amount ?? 0)
  }
  return result
}

export async function fetchNonRefundPurchaseReturnsTotals(
  tenantId: string,
  poIds: string[],
): Promise<Record<string, number>> {
  if (poIds.length === 0) return {}
  const data = await fetchByIdsChunked<{ original_purchase_id: string; total_amount: number }>(poIds, (batch) =>
    db('purchase_returns')
      .select('original_purchase_id, total_amount')
      .eq('tenant_id', tenantId)
      .in('original_purchase_id', batch)
      .neq('return_type', 'refund_cash')
      .neq('status', 'rejected'))
  const result: Record<string, number> = {}
  for (const r of data) {
    result[r.original_purchase_id] = (result[r.original_purchase_id] ?? 0) + (r.total_amount ?? 0)
  }
  return result
}

// Single source of truth for a PO's outstanding amount. Cancelled / void POs
// must contribute zero — their AP was reversed by the cancel RPC, so any
// non-zero outstanding here would double-count what was already cleared.
export function poOutstanding(po: {
  total: number | null
  amount_paid: number | null
  non_refund_returns_total?: number | null
  status?: string | null
}): number {
  if (po.status === 'cancelled' || po.status === 'void') return 0
  return Math.max(
    0,
    (po.total ?? 0) - (po.amount_paid ?? 0) - (po.non_refund_returns_total ?? 0),
  )
}

export async function fetchSalesOrderDetail(tenantId: string, orderId: string) {
  const { data, error } = await db('sales_orders')
    .select('id, order_number, subtotal, total, amount_paid, payment_status, status, created_at, sell_note, discount_amount, sale_discount_amount, sale_discount_percent, sale_discount_reason, tax_amount, shipping_charges, shipping_details, shipping_address, shipping_status, delivered_to, payment_due, location_id, created_by, share_token, representative_id, representative_name_snapshot, meta, location:warehouses!location_id(id, name), customers(id, name, business_name, phone, email, address)')
    .eq('tenant_id', tenantId)
    .eq('id', orderId)
    .maybeSingle()
  if (error) throw error
  return data as {
    id: string; order_number: string; subtotal: number | null; total: number; amount_paid: number
    payment_status: string; status: string; created_at: string
    sell_note: string | null
    discount_amount: number | null
    sale_discount_amount: number | null; sale_discount_percent: number | null; sale_discount_reason: string | null
    tax_amount: number | null; shipping_charges: number | null
    shipping_details: string | null; shipping_address: string | null; shipping_status: string | null
    delivered_to: string | null; payment_due: number | null
    location_id: string | null
    created_by: string | null
    share_token: string | null
    representative_id: string | null
    representative_name_snapshot: string | null
    meta: { opening_balance_bf?: boolean } | null
    location: { id: string; name: string } | null
    customers: { id: string; name: string; business_name: string | null; phone: string | null; email: string | null; address: string | null } | null
  } | null
}

// PR A2.4 — Manager+ override of the per-sale representative. Thin wrapper over
// the atomic RPC (mig 260): the sales_orders update + the sale_representative_changes
// audit row happen together, server-side. change_source is hard-coded 'manual_ui'
// in the RPC. changed_by is the audit stamp; auth.uid() drives the real authz.
// No period-lock check — a rep override posts nothing to the GL.
export async function reassignSaleRepresentative(params: {
  tenantId: string
  saleId: string
  newRepresentativeId: string | null
  reason?: string | null
  changedBy?: string | null
}): Promise<{
  changed: boolean
  oldRepresentativeId: string | null
  newRepresentativeId: string | null
  oldRepresentativeName: string | null
  newRepresentativeName: string | null
}> {
  const { data, error } = await (supabase as any).rpc('reassign_sale_representative', {
    p_tenant_id: params.tenantId,
    p_sale_id: params.saleId,
    p_new_representative_id: params.newRepresentativeId,
    p_reason: params.reason ?? null,
    p_changed_by: params.changedBy ?? null,
  })
  if (error) throw new Error(error.message)
  const r = (data ?? {}) as Record<string, unknown>
  return {
    changed: !!r.changed,
    oldRepresentativeId: (r.old_representative_id as string | null) ?? null,
    newRepresentativeId: (r.new_representative_id as string | null) ?? null,
    oldRepresentativeName: (r.old_representative_name as string | null) ?? null,
    newRepresentativeName: (r.new_representative_name as string | null) ?? null,
  }
}

export async function fetchSalesOrderItems(orderId: string) {
  const { data, error } = await db('sales_order_items')
    .select('id, quantity, unit_price, total, discount_type, discount_amount, discount_percent, tax_percent, tax_amount, price_inc_tax, bundle_id, bundle_name, products(id, name, sku)')
    .eq('sales_order_id', orderId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; quantity: number; unit_price: number; total: number
    discount_type: string | null; discount_amount: number | null; discount_percent: number | null
    tax_percent: number | null; tax_amount: number | null; price_inc_tax: number | null
    bundle_id: string | null; bundle_name: string | null
    products: { id: string; name: string; sku: string | null } | null
  }>
}

export async function fetchOrderPayments(orderId: string, orderType: 'sale' | 'purchase') {
  const { data, error } = await db('order_payments')
    .select('id, amount, paid_on, payment_method, note, created_at, accounts(name)')
    .eq('order_id', orderId)
    .eq('order_type', orderType)
    .neq('is_detached', true)
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; amount: number; paid_on: string; payment_method: string
    note: string | null; created_at: string
    accounts: { name: string } | null
  }>
}

// ─── Purchase summary (Buy page) ──────────────────────────────────────────────

export async function fetchPurchasesTotal(tid: string, from: string, to: string): Promise<number> {
  const data = await fetchAllPaged<{ total: number | null }>((pageFrom, pageTo) =>
    db('purchase_orders')
      .select('id, total')
      .eq('tenant_id', tid)
      .gte('created_at', from)
      .lte('created_at', `${to}T23:59:59`)
      .neq('status', 'cancelled')
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))
  return data.reduce((s, p) => s + (Number(p.total) || 0), 0)
}

export async function fetchPurchasesDailyTrend(tid: string, from: string, to: string): Promise<Array<{ day: string; purchases: number }>> {
  const dayMap: Record<string, number> = {}
  const start = new Date(from)
  const end = new Date(to)
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dayMap[d.toISOString().slice(0, 10)] = 0
  }

  const data = await fetchAllPaged<{ created_at: string; total: number | null }>((pageFrom, pageTo) =>
    db('purchase_orders')
      .select('id, created_at, total')
      .eq('tenant_id', tid)
      .gte('created_at', from)
      .lte('created_at', `${to}T23:59:59`)
      .neq('status', 'cancelled')
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))

  for (const p of data) {
    const key = p.created_at.slice(0, 10)
    if (key in dayMap) dayMap[key] += Number(p.total) || 0
  }

  return Object.keys(dayMap).sort().map((key) => ({
    day: key.slice(5), // MM-DD
    purchases: dayMap[key],
  }))
}

export async function fetchPurchasesMonthlyTrend(tid: string, from: string, to: string): Promise<Array<{ month: string; purchases: number }>> {
  const monthMap: Record<string, number> = {}
  const start = new Date(from)
  const end = new Date(to)
  for (let d = new Date(start.getFullYear(), start.getMonth(), 1); d <= end; d.setMonth(d.getMonth() + 1)) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    monthMap[key] = 0
  }

  const data = await fetchAllPaged<{ created_at: string; total: number | null }>((pageFrom, pageTo) =>
    db('purchase_orders')
      .select('id, created_at, total')
      .eq('tenant_id', tid)
      .gte('created_at', from)
      .lte('created_at', `${to}T23:59:59`)
      .neq('status', 'cancelled')
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))

  for (const p of data) {
    const key = p.created_at.slice(0, 7)
    if (key in monthMap) monthMap[key] += Number(p.total) || 0
  }

  return Object.keys(monthMap).sort().map((key) => {
    const [, m] = key.split('-')
    const month = new Date(2000, Number(m) - 1, 1).toLocaleString('en', { month: 'short' })
    return { month, purchases: monthMap[key] }
  })
}

// ─── Purchases (enhanced) ─────────────────────────────────────────────────────

export type PurchaseOrderFilters = {
  search?: string
  from?: string
  to?: string
  purchaseStatus?: string
  paymentStatus?: string
  locationId?: string
  createdBy?: string
}

export type PurchaseOrderRow = {
  id: string; order_number: string; waybill_number: string | null; supplier_invoice_ref: string | null
  total: number; amount_paid: number
  payment_status: string; status: string; delivery_status: string | null; created_at: string
  other_costs: number | null; supplier_id: string | null
  cancel_reason: string | null; cancelled_at: string | null
  suppliers: { id: string; name: string; business_name?: string | null; phone?: string | null } | null
  purchase_order_items: Array<{ quantity: number; quantity_received: number | null }> | null
  purchase_returns: Array<{ id: string }> | null
}

const PURCHASE_ORDERS_SELECT =
  'id, order_number, waybill_number, supplier_invoice_ref, created_at, status, delivery_status, total, amount_paid, payment_status, other_costs, supplier_id, location_id, cancel_reason, cancelled_at, suppliers(id, name, business_name, phone), warehouses(name), purchase_order_items(quantity, quantity_received), purchase_returns(id)'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyPurchaseFilters(q: any, tenantId: string, filters: PurchaseOrderFilters): any {
  q = q.eq('tenant_id', tenantId)
  if (filters.search) q = q.ilike('order_number', `%${filters.search}%`)
  if (filters.from) q = q.gte('created_at', filters.from + 'T00:00:00')
  if (filters.to) q = q.lte('created_at', filters.to + 'T23:59:59')
  if (filters.purchaseStatus) q = q.eq('status', filters.purchaseStatus)
  if (filters.paymentStatus && filters.paymentStatus !== 'all') q = q.eq('payment_status', filters.paymentStatus)
  if (filters.locationId) q = q.eq('location_id', filters.locationId)
  if (filters.createdBy) q = q.eq('created_by', filters.createdBy)
  return q
}

// One page of enhanced purchase rows + the exact total count for the same
// filters. Server-side paginated (replaces the old silent 50-row cap).
export async function fetchPurchaseOrdersEnhanced(
  tenantId: string,
  filters: PurchaseOrderFilters = {},
  pagination: { page?: number; pageSize?: number } = {},
): Promise<{ rows: PurchaseOrderRow[]; count: number }> {
  const page = Math.max(1, pagination.page ?? 1)
  const pageSize = pagination.pageSize ?? 50
  let q = db('purchase_orders').select(PURCHASE_ORDERS_SELECT, { count: 'exact' })
  q = applyPurchaseFilters(q, tenantId, filters)
  const { data, error, count } = await q
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1)
  if (error) throw error
  return { rows: (data ?? []) as PurchaseOrderRow[], count: count ?? 0 }
}

// Footer total: sum of `total` across ALL purchase orders matching the current
// filters. Paged so it never truncates.
export async function fetchPurchaseOrdersTotal(
  tenantId: string,
  filters: PurchaseOrderFilters = {},
): Promise<number> {
  const rows = await fetchAllPaged<{ total: number | null }>((from, to) =>
    applyPurchaseFilters(db('purchase_orders').select('total'), tenantId, filters)
      .order('id', { ascending: true })
      .range(from, to))
  return rows.reduce((s, r) => s + (r.total ?? 0), 0)
}

// Every row matching the current filters, for "export the whole view".
export async function fetchPurchaseOrdersForExport(
  tenantId: string,
  filters: PurchaseOrderFilters = {},
): Promise<PurchaseOrderRow[]> {
  const rows = await fetchAllPaged<PurchaseOrderRow>((from, to) =>
    applyPurchaseFilters(db('purchase_orders').select(PURCHASE_ORDERS_SELECT), tenantId, filters)
      .order('id', { ascending: true })
      .range(from, to))
  rows.sort((a, b) => b.created_at.localeCompare(a.created_at))
  return rows
}

export async function fetchPurchaseOrderDetail(tenantId: string, orderId: string) {
  const { data, error } = await db('purchase_orders')
    .select('id, order_number, waybill_number, created_at, status, delivery_status, subtotal, total, amount_paid, payment_status, other_costs, notes, additional_notes, received_date, supplier_id, cancelled_at, cancelled_by, cancel_reason, cancel_journal_id, suppliers(id, name, business_name, phone, email, address, city, state)')
    .eq('tenant_id', tenantId)
    .eq('id', orderId)
    .single()
  if (error) {
    console.error('Purchase detail query error:', error.message, 'id:', orderId)
    throw error
  }
  return data as {
    id: string; order_number: string; waybill_number: string | null; total: number; amount_paid: number
    payment_status: string; status: string; delivery_status: string | null; created_at: string
    subtotal: number | null; additional_notes: string | null; notes: string | null
    other_costs: number | null; received_date: string | null; supplier_id: string | null
    cancelled_at: string | null; cancelled_by: string | null; cancel_reason: string | null; cancel_journal_id: string | null
    suppliers: { id: string; name: string; business_name?: string | null; phone: string | null; email: string | null; address: string | null; city: string | null; state: string | null } | null
  } | null
}

export async function fetchPurchaseOrderItems(orderId: string) {
  const { data, error } = await db('purchase_order_items')
    .select('id, product_id, product_name, quantity, unit_price, net_unit_cost, total, discount, landed_cost, products(id, name, sku)')
    .eq('purchase_order_id', orderId)
  if (error) throw error
  return (data ?? []).map((item: {
    id: string; product_id: string; product_name: string | null; quantity: number
    unit_price: number; net_unit_cost: number | null; total: number
    discount: number | null; landed_cost: number | null
    products: { id: string; name: string; sku: string | null } | null
  }) => ({
    ...item,
    total_price: item.total,
    unit_cost_before_discount: item.unit_price,
    discount_percent: null,
    unit_cost_before_tax: item.net_unit_cost,
    tax_percent: null,
    tax_amount: null,
    subtotal_before_tax: null,
    profit_margin_percent: null,
    selling_price: null,
  })) as Array<{
    id: string; product_id: string; product_name: string | null; quantity: number
    unit_price: number; net_unit_cost: number | null; total: number; total_price: number
    discount: number | null; landed_cost: number | null
    discount_percent: number | null; unit_cost_before_discount: number | null
    unit_cost_before_tax: number | null; tax_percent: number | null; tax_amount: number | null
    subtotal_before_tax: number | null; profit_margin_percent: number | null; selling_price: number | null
    products: { id: string; name: string; sku: string | null } | null
  }>
}

// ─── Debtor / Creditor stats ──────────────────────────────────────────────────

export async function fetchDebtorStats(tenantId: string): Promise<{ total: number; count: number }> {
  const rows = await fetchAllPaged<{ customer_id: string | null; total: number | null; amount_paid: number | null }>((from, to) =>
    db('sales_orders').select('customer_id, total, amount_paid').eq('tenant_id', tenantId)
      .not('payment_status', 'in', '("cancelled","refunded","paid")')
      .order('id', { ascending: true }).range(from, to))
  const withBalance = rows.filter(r => (r.total ?? 0) > (r.amount_paid ?? 0))
  return {
    total: withBalance.reduce((s, r) => s + Math.max(0, (r.total ?? 0) - (r.amount_paid ?? 0)), 0),
    count: new Set(withBalance.map(r => r.customer_id).filter(Boolean)).size,
  }
}

export async function fetchCreditorStats(tenantId: string): Promise<{ total: number; count: number }> {
  const rows = await fetchAllPaged<{ supplier_id: string | null; total: number | null; amount_paid: number | null }>((from, to) =>
    db('purchase_orders').select('supplier_id, total, amount_paid').eq('tenant_id', tenantId)
      .not('payment_status', 'in', '("cancelled","refunded","paid")')
      .order('id', { ascending: true }).range(from, to))
  const withBalance = rows.filter(r => (r.total ?? 0) > (r.amount_paid ?? 0))
  return {
    total: withBalance.reduce((s, r) => s + Math.max(0, (r.total ?? 0) - (r.amount_paid ?? 0)), 0),
    count: new Set(withBalance.map(r => r.supplier_id).filter(Boolean)).size,
  }
}

// ─── Stock Entries (existing, moved below new functions) ─────────────────────

export async function fetchStockEntries(tenantId: string, entryType?: string, limit = 30) {
  let q = db('stock_entries')
    .select('id, quantity, entry_type, created_at, notes, products(name), warehouses(name)')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (entryType) q = q.eq('entry_type', entryType)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Array<{
    id: string; quantity: number; entry_type: string; created_at: string
    notes: string | null
    products: { name: string } | null
    warehouses: { name: string } | null
  }>
}

// ─── Dashboard v2 ─────────────────────────────────────────────────────────────

// Classify a tenant's P&L accounts by IFRS group_code — the same classification
// the Income Statement RPC uses (account → subgroup → group). This replaces the
// old hardcoded-account-code sets so Dashboard widgets match the Income Statement.
//   OR → revenue, OI → other income, CS → cost of sales,
//   SC/OC/AD/MK/OX → operating expenses, FN → finance costs, TX → taxation
type GLGroupSets = {
  revenueSet: Set<string>
  otherIncomeSet: Set<string>
  cogsSet: Set<string>
  opExpSet: Set<string>
  financeSet: Set<string>
  taxSet: Set<string>
  relevantIds: string[]
}

async function glGroupAccountSets(tenantId: string): Promise<GLGroupSets> {
  const { data } = await db('accounts')
    .select('id, account_subgroups(account_groups(code))')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)

  const revenue: string[] = [], otherIncome: string[] = [], cogs: string[] = []
  const opExp: string[] = [], finance: string[] = [], tax: string[] = []
  for (const a of (data ?? []) as Array<{
    id: string
    account_subgroups: { account_groups: { code: string } | null } | null
  }>) {
    const code = a.account_subgroups?.account_groups?.code
    if (!code) continue
    switch (code) {
      case 'OR': revenue.push(a.id); break
      case 'OI': otherIncome.push(a.id); break
      case 'CS': cogs.push(a.id); break
      case 'SC': case 'OC': case 'AD': case 'MK': case 'OX': opExp.push(a.id); break
      case 'FN': finance.push(a.id); break
      case 'TX': tax.push(a.id); break
    }
  }

  return {
    revenueSet: new Set(revenue),
    otherIncomeSet: new Set(otherIncome),
    cogsSet: new Set(cogs),
    opExpSet: new Set(opExp),
    financeSet: new Set(finance),
    taxSet: new Set(tax),
    relevantIds: [...revenue, ...otherIncome, ...cogs, ...opExp, ...finance, ...tax],
  }
}

export async function fetchGLKPIs(tenantId: string, from: string, to: string): Promise<{
  netSales: number; costOfSales: number; grossProfit: number; grossMargin: number
  otherIncome: number; totalExpenses: number; operatingProfit: number
  financeCosts: number; incomeTax: number
  netProfit: number; netMargin: number
}> {
  const income = await calculateIncomeStatement(tenantId, from, to)

  const netSales = income.revenue.total
  const costOfSales = income.costOfSales.total
  const grossProfit = income.grossProfit
  const otherIncome = income.otherIncome.total
  const totalExpenses = income.operatingExpenses.total
  const operatingProfit = income.operatingProfit
  const financeCosts = income.financeCosts.total
  const incomeTax = income.incomeTax
  const netProfit = income.profitAfterTax
  const grossMargin = netSales !== 0 ? (grossProfit / netSales) * 100 : 0
  const netMargin = netSales !== 0 ? (netProfit / netSales) * 100 : 0
  return {
    netSales, costOfSales, grossProfit, grossMargin,
    otherIncome, totalExpenses, operatingProfit, financeCosts, incomeTax,
    netProfit, netMargin,
  }
}

export async function fetchSalesReturnsTotal(tenantId: string, from: string, to: string): Promise<number> {
  const data = await fetchAllPaged<{ total_amount: number }>((pageFrom, pageTo) =>
    db('sale_returns')
      .select('id, total_amount')
      .eq('tenant_id', tenantId)
      .neq('status', 'rejected')
      .gte('return_date', from)
      .lte('return_date', to)
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))
  return data.reduce((sum, r) => sum + (r.total_amount ?? 0), 0)
}

export async function fetchCOGSForPeriod(tenantId: string, from: string, to: string): Promise<number> {
  const orders = await fetchAllPaged<{ id: string }>((pageFrom, pageTo) =>
    db('sales_orders')
      .select('id')
      .eq('tenant_id', tenantId)
      .neq('status', 'cancelled')
      .gte('created_at', from + 'T00:00:00')
      .lte('created_at', to + 'T23:59:59')
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))
  const orderIds = orders.map(o => o.id)
  if (!orderIds.length) return 0
  const items = await fetchByIdsChunked<{ quantity: number; products: { cost_price: number } | null }>(orderIds, (batch) =>
    db('sales_order_items')
      .select('quantity, products(cost_price)')
      .in('sales_order_id', batch))
  return items
    .reduce((sum, item) => sum + (item.quantity ?? 0) * (item.products?.cost_price ?? 0), 0)
}

export async function fetchExpensesForPeriod(tenantId: string, from: string, to: string): Promise<number> {
  const data = await fetchAllPaged<{ amount: number }>((pageFrom, pageTo) =>
    db('expenses')
      .select('id, amount')
      .eq('tenant_id', tenantId)
      .gte('expense_date', from)
      .lte('expense_date', to)
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))
  return data.reduce((sum, e) => sum + (e.amount ?? 0), 0)
}

export async function fetchExpenseBreakdown(
  tenantId: string,
  from: string,
  to: string,
): Promise<Array<{ name: string; value: number }>> {
  // Read expenses from the journals (via the Income Statement) rather than the
  // expenses table, so journal-only expenses are included and the breakdown
  // matches the Income Statement. Operating expenses (SC/OC/AD/MK/OX) plus
  // finance costs (FN), grouped per account.
  const income = await calculateIncomeStatement(tenantId, from, to)
  const lines = [...income.operatingExpenses.lines, ...income.financeCosts.lines]
  const map = new Map<string, number>()
  for (const line of lines) {
    const value = line.balance ?? 0
    if (value === 0) continue
    const name = line.name || 'Other'
    map.set(name, (map.get(name) ?? 0) + value)
  }
  return Array.from(map.entries())
    .map(([name, value]) => ({ name, value }))
    .filter(e => e.value > 0)
    .sort((a, b) => b.value - a.value)
}

export type InventoryWarehouseBalance = {
  warehouse_id: string
  warehouse_name: string
  units: number
  value: number
}

export type InventoryValueSummary = {
  value: number
  units: number
  warehouses: InventoryWarehouseBalance[]
}

export function summarizeInventory(
  warehouses: Array<{ id: string; name: string }>,
  stockRows: Array<{ product_id: string; warehouse_id: string; quantity: number | null }>,
  productCosts: Map<string, number>,
): InventoryValueSummary {
  const productIds = new Set(productCosts.keys())
  const byWarehouse = new Map<string, InventoryWarehouseBalance>()
  for (const w of warehouses) {
    byWarehouse.set(w.id, { warehouse_id: w.id, warehouse_name: w.name, units: 0, value: 0 })
  }
  for (const row of stockRows) {
    if (!productIds.has(row.product_id)) continue
    const bucket = byWarehouse.get(row.warehouse_id)
    if (!bucket) continue
    const qty = row.quantity ?? 0
    const cost = productCosts.get(row.product_id) ?? 0
    bucket.units += qty
    bucket.value += qty * cost
  }
  const list = Array.from(byWarehouse.values()).sort((a, b) =>
    a.warehouse_name.localeCompare(b.warehouse_name),
  )
  const units = list.reduce((s, w) => s + w.units, 0)
  const value = list.reduce((s, w) => s + w.value, 0)
  return { value, units, warehouses: list }
}

export async function fetchInventoryValue(tenantId: string): Promise<InventoryValueSummary> {
  const warehousesResult = await db('warehouses')
    .select('id, name')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .order('name')

  const warehouses = (warehousesResult.data ?? []) as Array<{ id: string; name: string }>
  if (!warehouses.length) {
    return { value: 0, units: 0, warehouses: [] }
  }

  const byWarehouse = new Map<string, InventoryWarehouseBalance>()
  for (const w of warehouses) {
    byWarehouse.set(w.id, { warehouse_id: w.id, warehouse_name: w.name, units: 0, value: 0 })
  }

  const stockRows = await fetchAllPaged<{ warehouse_id: string; quantity: number | null }>((from, to) =>
    db('current_stock')
      .select('warehouse_id, quantity')
      .eq('tenant_id', tenantId)
      .in('warehouse_id', warehouses.map((w) => w.id))
      .order('id', { ascending: true })
      .range(from, to))

  for (const row of stockRows) {
    const bucket = byWarehouse.get(row.warehouse_id)
    if (bucket) bucket.units += row.quantity ?? 0
  }

  const PAGE = 1000
  let from = 0
  for (;;) {
    const { data, error } = await db('stock_entries')
      .select('warehouse_id, quantity, total_value')
      .eq('tenant_id', tenantId)
      .range(from, from + PAGE - 1)
    if (error) throw error
    const rows = (data ?? []) as Array<{
      warehouse_id: string
      quantity: number | null
      total_value: number | null
    }>
    for (const r of rows) {
      const bucket = byWarehouse.get(r.warehouse_id)
      if (!bucket) continue
      const qty = Number(r.quantity ?? 0)
      const tv = Math.abs(Number(r.total_value ?? 0))
      bucket.value += qty < 0 ? -tv : tv
    }
    if (rows.length < PAGE) break
    from += PAGE
  }

  const list = Array.from(byWarehouse.values()).sort((a, b) =>
    a.warehouse_name.localeCompare(b.warehouse_name),
  )
  const units = list.reduce((s, w) => s + w.units, 0)
  const value = list.reduce((s, w) => s + w.value, 0)
  return { value, units, warehouses: list }
}

export async function fetchTopCustomers(tenantId: string, from: string, to: string, limit = 5) {
  const orders = await fetchAllPaged<{ customer_id: string | null; total: number; customers: { name: string; business_name: string | null } | null }>((pageFrom, pageTo) =>
    db('sales_orders')
      .select('id, customer_id, total, customers(name, business_name)')
      .eq('tenant_id', tenantId)
      .neq('status', 'cancelled')
      .gte('created_at', from + 'T00:00:00')
      .lte('created_at', to + 'T23:59:59')
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))
  const grouped = new Map<string, { name: string; revenue: number }>()
  for (const o of orders) {
    const key = o.customer_id ?? '__walk_in__'
    const name = o.customers ? (o.customers.business_name || o.customers.name) : 'Walk-in Customer'
    const existing = grouped.get(key) ?? { name, revenue: 0 }
    grouped.set(key, { name, revenue: existing.revenue + (o.total ?? 0) })
  }
  return Array.from(grouped.entries())
    .map(([id, v]) => ({ customer_id: id, customer_name: v.name, revenue: v.revenue }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit)
}

export async function fetchTopItemsWithRevenue(tenantId: string, from: string, to: string, limit = 5) {
  const orders = await fetchAllPaged<{ id: string }>((pageFrom, pageTo) =>
    db('sales_orders')
      .select('id')
      .eq('tenant_id', tenantId)
      .neq('status', 'cancelled')
      .gte('created_at', from + 'T00:00:00')
      .lte('created_at', to + 'T23:59:59')
      .order('id', { ascending: true })
      .range(pageFrom, pageTo))
  const orderIds = orders.map(o => o.id)
  if (!orderIds.length) return [] as Array<{ product_id: string; name: string; units: number; revenue: number }>
  const [items, returns] = await Promise.all([
    fetchByIdsChunked<{ product_id: string; quantity: number; total: number; products: { name: string } | null }>(orderIds, (batch) =>
      db('sales_order_items')
        .select('product_id, quantity, total, products(name)')
        .in('sales_order_id', batch)),
    fetchByIdsChunked<{ total_amount: number }>(orderIds, (batch) =>
      db('sale_returns')
        .select('total_amount')
        .eq('tenant_id', tenantId)
        .in('original_sale_id', batch)
        .neq('status', 'rejected')),
  ])
  const grouped = new Map<string, { name: string; units: number; revenue: number }>()
  let grossTotal = 0
  for (const item of items) {
    const pid = item.product_id
    const name = item.products?.name ?? pid
    const existing = grouped.get(pid) ?? { name, units: 0, revenue: 0 }
    grouped.set(pid, { name, units: existing.units + (item.quantity ?? 0), revenue: existing.revenue + (item.total ?? 0) })
    grossTotal += item.total ?? 0
  }
  // Returns aren't tied to line items, so apportion them proportionally across
  // products: scale each product's revenue by the period's net-of-returns factor.
  // Units stay as gross units sold.
  const returnsTotal = returns.reduce((s, r) => s + (r.total_amount ?? 0), 0)
  const factor = grossTotal > 0 ? Math.max(0, Math.min(1, 1 - returnsTotal / grossTotal)) : 1
  return Array.from(grouped.entries())
    .map(([id, v]) => ({ product_id: id, name: v.name, units: v.units, revenue: v.revenue * factor }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit)
}

export async function fetchDailyTrend(tenantId: string): Promise<Array<{ day: string; gross_sales: number; net_profit: number; expenses: number }>> {
  const now = new Date()
  const fromDate = subDays(now, 29)
  const fromStr = format(fromDate, 'yyyy-MM-dd')
  const toStr = format(now, 'yyyy-MM-dd')

  const [acctSets, journals] = await Promise.all([
    glGroupAccountSets(tenantId),
    fetchAllPaged<{ id: string; entry_date: string }>((from, to) =>
      db('journal_entries').select('id, entry_date').eq('tenant_id', tenantId)
        .or('is_posted.is.null,is_posted.eq.true')
        .or('is_voided.is.null,is_voided.eq.false')
        .gte('entry_date', fromStr).lte('entry_date', toStr)
        .order('id', { ascending: true })
        .range(from, to)),
  ])

  const empty = () => eachDayOfInterval({ start: fromDate, end: now }).map(d => ({ day: format(d, 'd MMM'), gross_sales: 0, net_profit: 0, expenses: 0 }))

  const { revenueSet, otherIncomeSet, cogsSet, opExpSet, financeSet, taxSet, relevantIds } = acctSets
  const journalDateMap = new Map<string, string>()
  for (const j of journals) {
    journalDateMap.set(j.id, j.entry_date)
  }
  const journalIds = Array.from(journalDateMap.keys())
  if (!journalIds.length || !relevantIds.length) return empty()

  const lines = await fetchByIdsChunked<{ journal_id: string; account_id: string; debit_amount: number; credit_amount: number }>(journalIds, (batch) =>
    db('journal_entry_lines')
      .select('journal_id, account_id, debit_amount, credit_amount')
      .eq('tenant_id', tenantId)
      .in('journal_id', batch)
      .in('account_id', relevantIds))

  type Agg = { revenue: number; otherIncome: number; cogs: number; opExp: number; finance: number; tax: number }
  const byDay = new Map<string, Agg>()
  for (const line of lines) {
    const entryDate = journalDateMap.get(line.journal_id)
    if (!entryDate) continue
    const dayStr = entryDate.slice(0, 10)
    if (!byDay.has(dayStr)) byDay.set(dayStr, { revenue: 0, otherIncome: 0, cogs: 0, opExp: 0, finance: 0, tax: 0 })
    const agg = byDay.get(dayStr)!
    const dr = line.debit_amount ?? 0, cr = line.credit_amount ?? 0
    const aid = line.account_id
    if (revenueSet.has(aid)) agg.revenue += cr - dr
    else if (otherIncomeSet.has(aid)) agg.otherIncome += cr - dr
    else if (cogsSet.has(aid)) agg.cogs += dr - cr
    else if (opExpSet.has(aid)) agg.opExp += dr - cr
    else if (financeSet.has(aid)) agg.finance += dr - cr
    else if (taxSet.has(aid)) agg.tax += dr - cr
  }

  return eachDayOfInterval({ start: fromDate, end: now }).map(d => {
    const dayStr = format(d, 'yyyy-MM-dd')
    const agg = byDay.get(dayStr) ?? { revenue: 0, otherIncome: 0, cogs: 0, opExp: 0, finance: 0, tax: 0 }
    const gross_sales = agg.revenue
    const net_profit = agg.revenue + agg.otherIncome - agg.cogs - agg.opExp - agg.finance - agg.tax
    return { day: format(d, 'd MMM'), gross_sales, net_profit, expenses: agg.opExp }
  })
}

export async function fetchMonthlyTrend(tenantId: string): Promise<Array<{ month: string; gross_sales: number; net_profit: number; expenses: number }>> {
  const now = new Date()
  const fromDate = startOfMonth(subMonths(now, 11))
  const fromStr = format(fromDate, 'yyyy-MM-dd')
  const toStr = format(now, 'yyyy-MM-dd')

  const [acctSets, journals] = await Promise.all([
    glGroupAccountSets(tenantId),
    fetchAllPaged<{ id: string; entry_date: string }>((from, to) =>
      db('journal_entries').select('id, entry_date').eq('tenant_id', tenantId)
        .or('is_posted.is.null,is_posted.eq.true')
        .or('is_voided.is.null,is_voided.eq.false')
        .gte('entry_date', fromStr).lte('entry_date', toStr)
        .order('id', { ascending: true })
        .range(from, to)),
  ])

  const empty = () => eachMonthOfInterval({ start: fromDate, end: now }).map(m => ({ month: format(m, 'MMM yy'), gross_sales: 0, net_profit: 0, expenses: 0 }))

  const { revenueSet, otherIncomeSet, cogsSet, opExpSet, financeSet, taxSet, relevantIds } = acctSets
  const journalDateMap = new Map<string, string>()
  for (const j of journals) {
    journalDateMap.set(j.id, j.entry_date)
  }
  const journalIds = Array.from(journalDateMap.keys())
  if (!journalIds.length || !relevantIds.length) return empty()

  const lines = await fetchByIdsChunked<{ journal_id: string; account_id: string; debit_amount: number; credit_amount: number }>(journalIds, (batch) =>
    db('journal_entry_lines')
      .select('journal_id, account_id, debit_amount, credit_amount')
      .eq('tenant_id', tenantId)
      .in('journal_id', batch)
      .in('account_id', relevantIds))

  type Agg = { revenue: number; otherIncome: number; cogs: number; opExp: number; finance: number; tax: number }
  const byMonth = new Map<string, Agg>()
  for (const line of lines) {
    const entryDate = journalDateMap.get(line.journal_id)
    if (!entryDate) continue
    const monthStr = entryDate.slice(0, 7)
    if (!byMonth.has(monthStr)) byMonth.set(monthStr, { revenue: 0, otherIncome: 0, cogs: 0, opExp: 0, finance: 0, tax: 0 })
    const agg = byMonth.get(monthStr)!
    const dr = line.debit_amount ?? 0, cr = line.credit_amount ?? 0
    const aid = line.account_id
    if (revenueSet.has(aid)) agg.revenue += cr - dr
    else if (otherIncomeSet.has(aid)) agg.otherIncome += cr - dr
    else if (cogsSet.has(aid)) agg.cogs += dr - cr
    else if (opExpSet.has(aid)) agg.opExp += dr - cr
    else if (financeSet.has(aid)) agg.finance += dr - cr
    else if (taxSet.has(aid)) agg.tax += dr - cr
  }

  return eachMonthOfInterval({ start: fromDate, end: now }).map(m => {
    const monthStr = format(m, 'yyyy-MM')
    const agg = byMonth.get(monthStr) ?? { revenue: 0, otherIncome: 0, cogs: 0, opExp: 0, finance: 0, tax: 0 }
    const gross_sales = agg.revenue
    const net_profit = agg.revenue + agg.otherIncome - agg.cogs - agg.opExp - agg.finance - agg.tax
    return { month: format(m, 'MMM yy'), gross_sales, net_profit, expenses: agg.opExp }
  })
}

// ─── Balance sheet card queries (journal-based) ───────────────────────────────
//
// Void-exclusion uses an embedded inner join to journal_entries, NOT a two-step
// "fetch valid journal ids → .in('journal_id', ids)". The two-step builds a
// company-wide multi-KB `journal_id=in.(…thousands of UUIDs…)` URL that proxies
// reject/truncate for large tenants, silently returning zero. Same pattern as
// src/lib/contactBalances.ts.

export async function fetchARBalances(tenantId: string): Promise<{ whatPeopleOweUs: number; customerAdvances: number }> {
  // Reuse the shared PAGINATED per-contact reader (single source of truth with
  // Aged Debtors / Contacts). A single un-paged fetch here truncated at ~1000
  // AR lines and under-reported on large tenants.
  const balByContact = await getAllCustomerBalances(supabase, tenantId)

  let whatPeopleOweUs = 0
  let customerAdvances = 0
  for (const bal of balByContact.values()) {
    if (bal > 0.005) whatPeopleOweUs += bal
    else if (bal < -0.005) customerAdvances += Math.abs(bal)
  }
  return { whatPeopleOweUs, customerAdvances }
}

export async function fetchAPBalance(tenantId: string): Promise<number> {
  const { suppliersPayable } = await fetchAPBalances(tenantId)
  return suppliersPayable
}

export async function fetchAPBalances(tenantId: string): Promise<{ suppliersPayable: number; suppliersAdvance: number }> {
  // Shared PAGINATED per-contact reader (getAllSupplierBalances already applies
  // the supplier credit−debit convention). Replaces a single un-paged fetch that
  // truncated at ~1000 AP lines.
  const balByContact = await getAllSupplierBalances(supabase, tenantId)

  let suppliersPayable = 0
  let suppliersAdvance = 0
  for (const bal of balByContact.values()) {
    if (bal > 0.005) suppliersPayable += bal
    else if (bal < -0.005) suppliersAdvance += Math.abs(bal)
  }
  return { suppliersPayable, suppliersAdvance }
}

export async function fetchAccruedExpenses(tenantId: string): Promise<number> {
  const data = await fetchAllPaged<{ amount: number | null }>((from, to) =>
    db('expenses')
      .select('id, amount')
      .eq('tenant_id', tenantId)
      .eq('expense_type', 'accrual')
      .neq('status', 'paid')
      .order('id', { ascending: true })
      .range(from, to))
  return data.reduce((sum, r) => sum + (r.amount ?? 0), 0)
}

export type CashBankAccountBalance = {
  id: string
  code: string | null
  name: string
  balance: number
}

export type CashBankBalanceSummary = {
  total: number
  accounts: CashBankAccountBalance[]
}

export function summarizeCashBalances(
  accounts: Array<{ id: string; code: string | null; name: string }>,
  lines: Array<{ account_id: string; debit_amount: number | null; credit_amount: number | null }>,
): CashBankBalanceSummary {
  const byAccount = new Map<string, number>()
  for (const line of lines) {
    const cur = byAccount.get(line.account_id) ?? 0
    byAccount.set(
      line.account_id,
      cur + (line.debit_amount ?? 0) - (line.credit_amount ?? 0),
    )
  }
  const enriched: CashBankAccountBalance[] = accounts.map((a) => ({
    id: a.id,
    code: a.code,
    name: a.name,
    balance: byAccount.get(a.id) ?? 0,
  }))
  enriched.sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''))
  const total = enriched.reduce((sum, a) => sum + a.balance, 0)
  return { total, accounts: enriched }
}

export async function fetchCashBankBalance(tenantId: string): Promise<CashBankBalanceSummary> {
  const { data: cashAccts } = await db('accounts')
    .select('id, code, name')
    .eq('tenant_id', tenantId)
    .eq('account_type', 'asset')
    .eq('is_payment_account', true)
    .eq('is_active', true)

  const accounts = ((cashAccts ?? []) as Array<{ id: string; code: string | null; name: string }>)
  if (!accounts.length) return { total: 0, accounts: [] }

  // Cash lines have no contact_id (not contact-tagged) — fetchAccountLines is the
  // paginated reader for account-level aggregation. Un-paged, this truncated at
  // ~1000 lines and under-reported the cash/bank position on active tenants.
  const lines = await fetchAccountLines(supabase, tenantId, accounts.map((a) => a.id))
  return summarizeCashBalances(
    accounts,
    lines.map(l => ({ account_id: l.accountId, debit_amount: l.debit, credit_amount: l.credit })),
  )
}

// ─── Online Orders (storefront) ─────────────────────────────────────────────
// Staff read their tenant's storefront orders directly: mig 281 grants SELECT on
// online_orders / online_order_items via `tenant_id = ANY(get_tenant_ids())`, so
// no reader RPC is needed. The list is a flat select — every display field is an
// order-time snapshot on online_orders — plus a single embedded join to the
// linked sale (only for its fulfilment/shipping status once paid).

export type OnlineOrderStatusBucket =
  | 'all' | 'paid' | 'awaiting_fulfilment' | 'needs_attention' | 'pending' | 'cancelled'

export type OnlineOrderFilters = {
  search?: string
  from?: string
  to?: string
  statusBucket?: OnlineOrderStatusBucket
}

export type OnlineOrderRow = {
  id: string
  order_number: string | null
  status: string
  status_reason: string | null
  payment_method: string | null
  buyer_marked_paid: boolean | null
  created_at: string
  customer_full_name: string
  customer_phone: string
  delivery_zone_name: string
  delivery_fee: number
  total: number
  sales_order_id: string | null
  sales_order: { id: string; shipping_status: string | null; payment_status: string | null } | null
}

const ONLINE_ORDERS_SELECT =
  'id, order_number, status, status_reason, payment_method, buyer_marked_paid, created_at, customer_full_name, customer_phone, delivery_zone_name, delivery_fee, total, sales_order_id, sales_order:sales_orders!sales_order_id(id, shipping_status, payment_status)'

// Shared payment/order pill (POD.4) — the ONE mapping used by BOTH the merchant list
// PAYMENT column and the detail Payment Status, so POD reads clearly (never the raw
// 'pod_confirmed' enum) and identically across surfaces. Keyed off status +
// payment_method: an uncollected POD is a distinct blue "Pay on delivery"; a
// collected POD is green "Paid — on delivery" (distinguished from a card "Paid" at a
// glance); everything else is unchanged from the pre-POD.4 list treatment.
export function onlineOrderPaymentPill(
  o: { status: string; payment_method?: string | null; status_reason?: string | null },
): { cls: string; label: string; title?: string } {
  switch (o.status) {
    case 'pod_confirmed':
      return { cls: 'badge-blue', label: 'Pay on delivery', title: 'Payable in cash on delivery' }
    case 'awaiting_transfer':
      return { cls: 'badge-blue', label: 'Bank transfer — awaiting confirmation', title: 'Buyer to transfer; confirm when the money arrives' }
    case 'paid':
      return o.payment_method === 'pod'
        ? { cls: 'badge-green', label: 'Paid — on delivery' }
        : o.payment_method === 'bank_transfer'
          ? { cls: 'badge-green', label: 'Paid — transfer' }
          : { cls: 'badge-green', label: 'Paid' }
    case 'pending_payment':
      return { cls: 'badge-orange', label: 'Awaiting payment' }
    case 'failed':
      return o.status_reason === 'stock_unavailable'
        ? { cls: 'badge-red', label: 'Needs attention', title: 'Payment taken but stock unavailable' }
        : { cls: 'badge-red', label: 'Failed', title: o.status_reason ?? undefined }
    case 'cancelled':
      return { cls: 'badge-gray', label: 'Cancelled' }
    default:
      return { cls: 'badge-gray', label: o.status }
  }
}

const FULFILLED_SHIPPING_STATUSES = ['delivered', 'cancelled']

// Apply tenant + list filters. Status buckets that depend on the linked sale's
// shipping_status ('awaiting_fulfilment') can't be expressed as a column filter
// across the join, so they narrow to status='paid' here and are refined in JS.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyOnlineOrderFilters(q: any, tenantId: string, filters: OnlineOrderFilters): any {
  q = q.eq('tenant_id', tenantId)
  if (filters.search) {
    const s = filters.search.replace(/,/g, '')
    q = q.or(`order_number.ilike.%${s}%,customer_full_name.ilike.%${s}%,customer_phone.ilike.%${s}%`)
  }
  if (filters.from) q = q.gte('created_at', filters.from + 'T00:00:00')
  if (filters.to) q = q.lte('created_at', filters.to + 'T23:59:59')

  switch (filters.statusBucket) {
    case 'paid':
    case 'awaiting_fulfilment':
      q = q.eq('status', 'paid'); break
    case 'needs_attention':
      q = q.eq('status', 'failed'); break
    case 'pending':
      q = q.eq('status', 'pending_payment'); break
    case 'cancelled':
      q = q.eq('status', 'cancelled'); break
    // 'all' / undefined → no status filter
  }
  return q
}

function isAwaitingFulfilment(row: OnlineOrderRow): boolean {
  if (row.status !== 'paid') return false
  const ship = (row.sales_order?.shipping_status ?? '').toLowerCase()
  return !FULFILLED_SHIPPING_STATUSES.includes(ship)
}

// One page of online orders + the exact count for the same filters. Newest first.
// The 'awaiting_fulfilment' bucket is post-filtered on the joined shipping_status;
// because that can't be a server-side predicate, its count is only exact within
// the fetched page — acceptable for a low-volume storefront feed, and the pager
// still works off the server count for every other bucket.
export async function fetchOnlineOrders(
  tenantId: string,
  filters: OnlineOrderFilters = {},
  pagination: { page?: number; pageSize?: number } = {},
): Promise<{ rows: OnlineOrderRow[]; count: number }> {
  const page = Math.max(1, pagination.page ?? 1)
  const pageSize = pagination.pageSize ?? 50

  let q = db('online_orders').select(ONLINE_ORDERS_SELECT, { count: 'exact' })
  q = applyOnlineOrderFilters(q, tenantId, filters)
  const { data, error, count } = await q
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1)
  if (error) throw error
  let rows = (data ?? []) as OnlineOrderRow[]

  if (filters.statusBucket === 'awaiting_fulfilment') {
    rows = rows.filter(isAwaitingFulfilment)
    return { rows, count: rows.length }
  }
  return { rows, count: count ?? 0 }
}

export type OnlineOrderKpis = {
  ordersToday: number
  revenueThisMonth: number
  awaitingFulfilment: number
  needsAttention: number
  stockUnavailable: number
  // POD (POD.2): orders confirmed but awaiting delivery + cash collection (POD.3).
  // A real action item for the merchant, so it folds into the unattended count.
  podConfirmed: number
  // Bank transfer (BT.3): orders placed, awaiting the merchant confirming the money
  // arrived. A real action item too, so it also folds into the unattended count.
  bankAwaitingConfirmation: number
}

// KPI aggregate over ALL of a tenant's online orders. Paginated per the
// aggregate-read rule (an un-paged select silently caps at ~1000 rows). Storefront
// volume is low today, but the pager keeps the KPIs correct as it grows. The
// today/month boundaries are computed here (not passed in) so callers share one
// query key — the list KPI row, the Sell-tab badge and the sidebar tile all read
// a single cached result.
export async function fetchOnlineOrderKpis(tenantId: string): Promise<OnlineOrderKpis> {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const todayStart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T00:00:00`
  const monthStart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01T00:00:00`

  const rows = await fetchAllPaged<OnlineOrderRow>((from, to) =>
    db('online_orders')
      .select('id, status, status_reason, created_at, total, sales_order_id, sales_order:sales_orders!sales_order_id(id, shipping_status, payment_status)')
      .eq('tenant_id', tenantId)
      .order('id', { ascending: true })
      .range(from, to))

  const kpis: OnlineOrderKpis = {
    ordersToday: 0, revenueThisMonth: 0, awaitingFulfilment: 0, needsAttention: 0, stockUnavailable: 0,
    podConfirmed: 0, bankAwaitingConfirmation: 0,
  }
  for (const r of rows) {
    if (r.created_at >= todayStart) kpis.ordersToday += 1
    if (r.status === 'paid' && r.created_at >= monthStart) kpis.revenueThisMonth += r.total ?? 0
    if (isAwaitingFulfilment(r)) kpis.awaitingFulfilment += 1
    if (r.status === 'pod_confirmed') kpis.podConfirmed += 1
    if (r.status === 'awaiting_transfer') kpis.bankAwaitingConfirmation += 1
    if (r.status === 'failed') {
      kpis.needsAttention += 1
      if (r.status_reason === 'stock_unavailable') kpis.stockUnavailable += 1
    }
  }
  return kpis
}

// The action backlog: orders a staffer still has to handle. Paid-but-not-delivered
// (awaiting fulfilment) plus money-taken-but-out-of-stock (needs manual resolve).
// Excludes delivered, cancelled and never-paid orders — so the number ticks down
// as orders are marked delivered and clears when everything is handled. Drives the
// red Sell-tab badge and folds into the sidebar Trading-tile badge.
export function unattendedOnlineOrderCount(kpis: OnlineOrderKpis | undefined | null): number {
  if (!kpis) return 0
  return kpis.awaitingFulfilment + kpis.stockUnavailable + kpis.podConfirmed + kpis.bankAwaitingConfirmation
}

export type OnlineOrderItemRow = {
  id: string
  product_name: string
  sku: string | null
  quantity: number
  unit_price: number
  line_total: number
}

export type OnlineOrderDetail = {
  id: string
  order_number: string | null
  status: string
  status_reason: string | null
  currency: string
  created_at: string
  expires_at: string | null
  customer_full_name: string
  customer_email: string
  customer_phone: string
  shipping_recipient_name: string
  shipping_phone: string
  shipping_street_address: string
  shipping_street_address_2: string | null
  shipping_landmark: string | null
  shipping_lga: string | null
  shipping_city: string
  shipping_state: string
  shipping_country: string
  shipping_postal_code: string | null
  shipping_delivery_notes: string | null
  delivery_zone_name: string
  delivery_fee: number
  subtotal: number
  tax_amount: number
  discount_amount: number
  total: number
  payment_provider: string | null
  payment_method: string | null
  payment_reference: string | null
  payment_fee_amount: number | null
  payment_completed_at: string | null
  buyer_marked_paid: boolean | null
  sales_order_id: string | null
  sales_order: { id: string; order_number: string | null; shipping_status: string | null; payment_status: string | null } | null
  items: OnlineOrderItemRow[]
}

const ONLINE_ORDER_DETAIL_SELECT =
  'id, order_number, status, status_reason, currency, created_at, expires_at, ' +
  'customer_full_name, customer_email, customer_phone, ' +
  'shipping_recipient_name, shipping_phone, shipping_street_address, shipping_street_address_2, ' +
  'shipping_landmark, shipping_lga, shipping_city, shipping_state, shipping_country, shipping_postal_code, shipping_delivery_notes, ' +
  'delivery_zone_name, delivery_fee, subtotal, tax_amount, discount_amount, total, ' +
  'payment_provider, payment_method, payment_reference, payment_fee_amount, payment_completed_at, buyer_marked_paid, sales_order_id, ' +
  'sales_order:sales_orders!sales_order_id(id, order_number, shipping_status, payment_status), ' +
  'items:online_order_items(id, product_name, sku, quantity, unit_price, line_total)'

// One read for the order detail: the online_orders row + its snapshot items + the
// linked sale (for fulfilment/payment status and the deep-link). RLS already
// scopes to the caller's tenant; the explicit tenant filter keeps the index hit.
export async function fetchOnlineOrder(tenantId: string, id: string): Promise<OnlineOrderDetail | null> {
  const { data, error } = await db('online_orders')
    .select(ONLINE_ORDER_DETAIL_SELECT)
    .eq('tenant_id', tenantId)
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return (data ?? null) as OnlineOrderDetail | null
}

// Advance fulfilment on the LINKED sale — online orders carry no fulfilment field
// of their own (investigation §3), so the status rides on sales_orders.shipping_status
// exactly like in-person sales. Writes only shipping_status (+ delivered_to when
// marking delivered); never touches money, stock or online_orders.
export async function updateSaleShippingStatus(params: {
  tenantId: string
  salesOrderId: string
  shippingStatus: string
  deliveredTo?: string | null
}): Promise<void> {
  const patch: Record<string, unknown> = { shipping_status: params.shippingStatus }
  if (params.deliveredTo !== undefined) patch.delivered_to = params.deliveredTo
  const { error } = await db('sales_orders')
    .update(patch)
    .eq('id', params.salesOrderId)
    .eq('tenant_id', params.tenantId)
  if (error) throw error
}

// POD.3 — the money step. A pay-on-delivery order (status='pod_confirmed') is
// collected at the door: the RPC recomputes every amount server-side, does a locked
// stock re-check, posts the goods sale (subtotal+VAT) + a separate delivery-income
// journal (NO gateway fee) into the chosen cash/bank account, then flips the order
// to paid + the linked sale to delivered. The RPC enforces caller tenant scope; it
// trusts NO client amount (only the order id + which account received the money).
export type ConfirmPodResult =
  | { ok: true; already?: boolean; sales_order_id: string; order_number: string | null
      journal_id?: string; delivery_journal_id?: string | null; amount_collected?: number; account_id?: string }
  | { ok: false; reason: string; items?: { product_id: string | null; name: string | null; reason: string; requested?: number; available?: number }[] }

export async function confirmPodCollection(params: {
  orderId: string
  accountId: string
}): Promise<ConfirmPodResult> {
  const { data, error } = await (supabase as any).rpc('confirm_pod_collection', {
    p_order_id: params.orderId,
    p_account_id: params.accountId,
  })
  if (error) throw error
  return data as ConfirmPodResult
}

// POD.5 — merchant cancel of an uncollected POD order (customer refused / fake /
// abusive). A clean status flip: nothing was posted or reserved, so no sale to
// reverse, no stock to return, no refund. The RPC enforces caller tenant scope and
// only touches a pod_confirmed order (paid → already_paid; other statuses rejected).
export type CancelPodResult =
  | { ok: true; already?: boolean; order_number: string | null; status: string; status_reason?: string }
  | { ok: false; reason: string }

export async function cancelPodOrder(params: {
  orderId: string
  reason?: string | null
}): Promise<CancelPodResult> {
  const { data, error } = await (supabase as any).rpc('cancel_pod_order', {
    p_order_id: params.orderId,
    p_reason: params.reason ?? null,
  })
  if (error) throw error
  return data as CancelPodResult
}

// BT.3 — the merchant money step for a bank-transfer order (status='awaiting_transfer').
// Mirrors confirm_pod_collection EXACTLY (goods+delivery post into the chosen bank/cash
// account, NO Paystack fee, locked stock re-check → hold-and-notify, idempotent, tenant
// scope enforced in the RPC) with ONE difference: it does NOT mark the linked sale
// delivered — a transfer confirms the money arrived; the goods ship later (the order
// flips to paid and enters the normal awaiting-fulfilment flow). Recognises money ONLY
// here; the shopper's "I've paid" (buyer_marked_paid) never posts anything.
export type ConfirmBankTransferResult =
  | { ok: true; already?: boolean; sales_order_id: string; order_number: string | null
      journal_id?: string; delivery_journal_id?: string | null; amount_collected?: number; account_id?: string }
  | { ok: false; reason: string; items?: { product_id: string | null; name: string | null; reason: string; requested?: number; available?: number }[] }

export async function confirmBankTransferPayment(params: {
  orderId: string
  accountId: string
}): Promise<ConfirmBankTransferResult> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('confirm_bank_transfer_payment', {
    p_order_id: params.orderId,
    p_account_id: params.accountId,
  })
  if (error) throw error
  return data as ConfirmBankTransferResult
}

// BT.3 — merchant cancel of an uncollected bank-transfer order (buyer never paid / fake).
// Mirrors cancel_pod_order: a clean status flip on an awaiting_transfer order → cancelled
// (paid → already_paid; other statuses rejected). No sale/stock/journal. Tenant-scoped.
export type CancelBankTransferResult =
  | { ok: true; already?: boolean; order_number: string | null; status: string; status_reason?: string }
  | { ok: false; reason: string }

export async function cancelBankTransferOrder(params: {
  orderId: string
  reason?: string | null
}): Promise<CancelBankTransferResult> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('cancel_bank_transfer_order', {
    p_order_id: params.orderId,
    p_reason: params.reason ?? null,
  })
  if (error) throw error
  return data as CancelBankTransferResult
}
